import React from 'react';
import styled from '@emotion/styled';
import { CTA } from '../../../components/CTA';
import { __ } from '../../../helpers/TranslationService';
import { hasHotelServiceCharges } from '../../../helpers/hotelServiceChargesHelper';
import * as InclusivesIcons from '../helper/InclusiveIcons';
import { EuroIcon } from '../helper/InclusiveIcons';
import { eventTracker } from '../../../helpers/EventTracker/EventTracker';
import { TrackComponent } from '../../../components/TrackComponent';
import { Link } from '../../../components/Link';
import { BreakpointsEnum } from '../../../helpers/breakpoints';
import { IMobileSearchResultCruise, IMobileSearchResultSail } from '../../../interfaces/ISearchApi';
import { badgeTypes } from './SearchResultItem/helpers/search-helpers';

interface IProps {
    cruise?: IMobileSearchResultCruise;
    selectedSail?: IMobileSearchResultSail;
    portalId?: string;
    packagesUrl?: string;
    typeOfBadge?: string;
    experienceDescription?: string;
    experienceUrl?: string;
    experienceTitle?: string;
}

const style: Record<string, React.CSSProperties> = {
    icon: {
        fill: '#7D7D7D',
    },
};

const eventTrackingData = {
    category: 'search results',
    event: 'click',
    action: 'search result click',
    label: 'dreamlines package',
};

const LinkStyled = styled(Link)`
    display: flex;
    justify-content: center;
    width: 100%;
    margin-top: 16px;
    color: ${({ theme }) => theme.link.primary};
    &:hover {
        color: ${({ theme }) => theme.link.primaryHover};
    }
`;

const ExperienceTitle = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    align-items: flex-start;
`;

const ExperienceSubtitle = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 400;
`;

const ItemOverlayWrapper = styled.div`
    min-height: 400px;
    min-width: 300px;
    @media (min-width: ${BreakpointsEnum.md}) {
        min-width: 550px;
    }
`;

const ItemOverlayInfo = styled.div`
    min-height: 300px;
`;

const OverlayFooter = styled.div`
    display: flex;
    flex-direction: row;
`;

export const SearchResultItemOverlay = ({
    cruise,
    selectedSail,
    portalId,
    packagesUrl,
    typeOfBadge,
    experienceDescription,
    experienceUrl,
    experienceTitle,
}: IProps) => {
    const { cruisePageUrl } = cruise;
    const cruisePageUrlWithoutHash = cruisePageUrl.slice(0, cruisePageUrl.lastIndexOf('#'));
    const searchResultItemUrl = cruisePageUrlWithoutHash + '#selectedSail=' + selectedSail.sailNid;

    const TrackableLink = TrackComponent(Link, eventTrackingData);

    const includesHotelServiceCharges = hasHotelServiceCharges(
        portalId,
        cruise.companyTitle,
        cruise.shipTitle,
        cruise.earliestDeparture,
    );

    const onClick = () => {
        eventTracker.track(eventTrackingData);
    };

    const isExperienceBadgeType = typeOfBadge === badgeTypes.experience;
    const isPackageBadgeType = typeOfBadge === badgeTypes.package;

    return (
        <ItemOverlayWrapper>
            <ExperienceTitle className="searchResultItemOverlay__headline">
                {isExperienceBadgeType
                    ? __('Dreamlines Experience', 'dreamlines')
                    : __('Dreamlines Package', 'dreamlines')}{' '}
                {isExperienceBadgeType && (
                    <ExperienceSubtitle>
                        {__('experienceDialogDescription', 'dreamlines')}
                    </ExperienceSubtitle>
                )}
            </ExperienceTitle>

            <div className="searchResultItemOverlay__info">
                {isExperienceBadgeType
                    ? experienceDescription
                    : __(
                          'Discover some of the most dazzling destinations with exclusive benefits tailored to your needs and desires.',
                          'dreamlines',
                      )}
            </div>
            <div className="searchResultItemOverlay__title">
                {__('This Package Include', 'dreamlines')}
            </div>

            <ItemOverlayInfo className="searchResultItemOverlay__infoArea">
                <ul className="searchResultItemOverlay__list">
                    {cruise?.icons.map((icon, i) => {
                        const IconComp = InclusivesIcons[icon?.icon];
                        if (!IconComp) {
                            return null;
                        }
                        return (
                            <li key={`${i}`} className="searchResultItemOverlay__listItem">
                                <span className={`searchResultItemOverlay__item__icon`}>
                                    <IconComp width={25} height={25} style={style.icon} />
                                </span>
                                <span className={`searchResultItemOverlay__item__title`}>
                                    {__(icon.text, 'dreamlines')}
                                </span>
                            </li>
                        );
                    })}
                    {includesHotelServiceCharges && (
                        <li className={`searchResultItemOverlay__listItem`}>
                            <span className={`searchResultItemOverlay__item__icon`}>
                                <EuroIcon width={25} height={25} style={style.icon} />
                            </span>
                            <span className="hotelServiceChargesLabel ">
                                {__('incl. hotel service charges', 'dreamlines')}
                            </span>
                        </li>
                    )}
                </ul>

                <div className="hr" />

                <OverlayFooter>
                    {isPackageBadgeType && (
                        <LinkStyled href={packagesUrl} followLink>
                            <span>{__('More about Dreamlines Package', 'dreamlines')}</span>
                        </LinkStyled>
                    )}
                    {isExperienceBadgeType && !!experienceUrl && (
                        <LinkStyled href={experienceUrl} followLink>
                            {`${__('Learn More about', 'dreamlines')} ${experienceTitle}`}
                        </LinkStyled>
                    )}
                    <TrackableLink
                        className="searchResultItemOverlay__button__wrapper"
                        href={searchResultItemUrl}
                    >
                        <CTA modal={true} onClick={onClick} className="CTA">
                            <span>{__('View Offer', 'dreamlines')}</span>
                        </CTA>
                    </TrackableLink>
                </OverlayFooter>
            </ItemOverlayInfo>
        </ItemOverlayWrapper>
    );
};
